import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { KineticLine } from "./Reveal";
import { WaButton } from "./WhatsAppIcon";
import { WA_DEFAULT_LINK, CNPJ, HOURS } from "../constants/site";

const HERO_IMG =
    "https://images.unsplash.com/photo-1588854337115-1c67d9247e4d?q=80&w=1400&auto=format&fit=crop";

export const Hero = () => {
    const ref = useRef(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 60, damping: 15 });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 60, damping: 15 });

    const handleMove = (e) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    const handleLeave = () => {
        mx.set(0);
        my.set(0);
    };

    return (
        <section
            id="inicio"
            ref={ref}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
            className="relative min-h-screen overflow-hidden bg-white"
            data-testid="hero-section"
        >
            <div className="absolute inset-0 blueprint-grid" aria-hidden="true" />
            <div
                className="absolute -top-40 right-[-10%] w-[720px] h-[720px] rounded-full opacity-60 blur-3xl pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(255,121,0,0.16) 0%, rgba(255,121,0,0) 65%)" }}
                aria-hidden="true"
            />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 min-h-screen grid lg:grid-cols-12 items-center gap-12 lg:gap-8 pt-28 pb-24 lg:pt-20 lg:pb-16">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="inline-flex items-center gap-2.5 rounded-full border border-brand/30 bg-brand-soft px-4 py-1.5"
                        data-testid="hero-badge"
                    >
                        <span className="w-2 h-2 rounded-full bg-brand" />
                        <span className="font-technical font-semibold uppercase tracking-[0.16em] text-xs sm:text-sm text-graphite">
                            Uberlândia–MG · Atendimento pelo WhatsApp
                        </span>
                    </motion.div>

                    <h1 className="mt-6 font-display font-black tracking-tight leading-[0.95] text-graphite text-[clamp(2.9rem,7.2vw,6.2rem)]">
                        <KineticLine delay={0.25}>Conserto de</KineticLine>
                        <KineticLine delay={0.38} className="text-brand">Geladeiras</KineticLine>
                        <KineticLine delay={0.51}>em Uberlândia</KineticLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-6 max-w-xl text-base sm:text-lg text-graphite/70 leading-relaxed"
                    >
                        Assistência técnica para geladeiras <strong className="font-semibold text-graphite">Frost Free</strong>,{" "}
                        <strong className="font-semibold text-graphite">Inverter</strong> e{" "}
                        <strong className="font-semibold text-graphite">Side by Side</strong>. Fale direto com o técnico
                        e resolva sem complicação.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-9 flex flex-wrap items-center gap-4 sm:gap-5"
                    >
                        <WaButton href={WA_DEFAULT_LINK} testId="hero-whatsapp-cta">
                            Solicitar atendimento pelo WhatsApp
                        </WaButton>
                        <a
                            href="#defeitos"
                            data-testid="hero-secondary-link"
                            className="group inline-flex items-center gap-2 font-display font-semibold text-graphite/70 hover:text-brand transition-colors"
                        >
                            Ver defeitos comuns
                            <span className="inline-block transition-transform group-hover:translate-y-1">↓</span>
                        </a>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-technical font-medium uppercase tracking-[0.14em] text-xs sm:text-sm text-graphite/50"
                        data-testid="hero-trust-row"
                    >
                        <li>Uberlândia–MG</li>
                        <li className="text-brand">•</li>
                        <li>{HOURS}</li>
                        <li className="text-brand">•</li>
                        <li>CNPJ {CNPJ}</li>
                    </motion.ul>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.94, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.5 }}
                    className="lg:col-span-5 [perspective:1400px]"
                    data-testid="hero-visual"
                >
                    <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
                        <div
                            className="absolute -inset-4 sm:-inset-6 rounded-[2.5rem] border-2 border-brand/25 pointer-events-none"
                            aria-hidden="true"
                        />
                        <div className="relative overflow-hidden rounded-[2rem] shadow-[0_40px_80px_-30px_rgba(48,52,58,0.45)]">
                            <img
                                src={HERO_IMG}
                                alt="Geladeira moderna em cozinha planejada — conserto de geladeiras em Uberlândia"
                                className="w-full aspect-[4/5] sm:aspect-[5/5] lg:aspect-[4/5] object-cover"
                                loading="eager"
                                fetchPriority="high"
                            />
                            <div
                                className="absolute inset-0 bg-gradient-to-t from-graphite/35 via-transparent to-transparent"
                                aria-hidden="true"
                            />
                        </div>
                        <div
                            className="absolute -bottom-5 -left-4 sm:-left-8 flex items-center gap-3 rounded-2xl bg-white/95 backdrop-blur px-5 py-4 shadow-[0_18px_40px_-15px_rgba(48,52,58,0.4)] border border-mist"
                            style={{ transform: "translateZ(60px)" }}
                            data-testid="hero-floating-chip"
                        >
                            <span className="relative flex w-3 h-3">
                                <span className="absolute inline-flex w-full h-full rounded-full bg-wa opacity-60 animate-ping" />
                                <span className="relative inline-flex w-3 h-3 rounded-full bg-wa" />
                            </span>
                            <div>
                                <p className="font-display font-bold text-sm text-graphite leading-tight">
                                    Atendimento agora
                                </p>
                                <p className="font-technical uppercase tracking-[0.12em] text-xs text-graphite/55">
                                    pelo WhatsApp
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
